import React, { useState, useEffect } from "react";
import axios from "axios";
import TableGrid from "../tableComponents/TableGrid";
import DownloadButton from "../../components/downloadButton";
import {
  eliminateOjectNotTruthyValues,
  createOrderModel,
  createPaginationModel,
} from "../../lib/helperFuncs";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
} from "@mui/material";
import { useAuthContext } from "../../context/AuthContext";

const DeptCertificatesToAADEComponent = ({
  endpoint,
  filters,
  deptCertificatesColumns,
  downloadUrl,
  showCheckbox,
  isProblematic,
}) => {
  const [_pageSize, setPageSize] = useState(5);
  const [_page, setPage] = useState(0);
  const [sortModel, setSortModel] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isFetching, setIsFetching] = useState(true);
  const [rows, setRowsArray] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const { setNotification } = useAuthContext();
  const [open, setOpen] = useState(false);
  const [openAll, setOpenAll] = useState(false);

  const handleSortModelChange = (newModel) => {
    setSortModel(newModel);
  };

  const handleRowSelection = (newSelectedRows, details) => {
    setSelectedRows(newSelectedRows);
  };

  const paginationProps = {
    fetchingFunction: (pageSize, page) => {
      setPageSize(pageSize);
      setPage(page);
    },
    setPageSize: setPageSize,
    page: _page,
    setPage: setPage,
  };

  const handleRows = async (page, pageSize, sortModel) => {
    try {
      setIsFetching(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/${endpoint}`,
        {
          params: eliminateOjectNotTruthyValues({
            ...filters,
            ...createPaginationModel(page, pageSize),
            ...createOrderModel(sortModel),
          }),
          withCredentials: true,
        },
      );

      setRowsArray(response.data.records);
      setTotalCount(response.data.total_count);
    } catch (e) {
      console.error(e);
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    let active = true;

    (async () => {
      handleRows(_page, _pageSize, sortModel);

      if (!active) {
        return;
      }
    })();

    return () => {
      active = false;
    };
  }, [sortModel, _page, _pageSize, filters]);

  const handleResendClick = async () => {
    try {
      setIsFetching(true);
      setOpen(false);
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/updateProblematicAll`,
        { filters, identifiers: selectedRows },
        {
          withCredentials: true,
        },
      );
      setIsFetching(false);
      setNotification({
        open: true,
        color: "success",
        message: "Επιτυχής Επαναποστολή!",
      });

      handleRows(_page, _pageSize, sortModel);

      setSelectedRows([]);
    } catch (error) {
      setOpen(false);
      setIsFetching(false);
      setNotification({
        open: true,
        color: "error",
        message: "Σφάλμα κατά την ενημέρωση, Προσπαθήστε ξανά",
      });
    }
  };

  const handleResendAllClick = async () => {
    try {
      setOpenAll(false);
      setIsFetching(true);
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/problematicAllData`,
        { filters, identifiers: selectedRows },
        {
          withCredentials: true,
        },
      );

      setIsFetching(false);
      setNotification({
        open: true,
        color: "success",
        message: "Επιτυχής Ενημέρωση για όλα τα προβληματικά!",
      });
      setRowsArray([]);
      setSelectedRows([]);
      handleRows(_page, _pageSize, sortModel);
    } catch (error) {
      setIsFetching(false);
      setNotification({
        open: true,
        color: "error",
        message: "Σφάλμα κατά την ενημέρωση, Προσπαθήστε ξανά",
      });
    }
  };

  const handleClose = () => {
    setOpen(false);
    setOpenAll(false);
  };

  return (
    <>
      <div style={{ display: "relative", width: "100%" }}>
        <TableGrid
          onSelectionModelChange={showCheckbox ? handleRowSelection : undefined}
          {...paginationProps}
          selectionModel={showCheckbox ? selectedRows : undefined}
          sortModel={sortModel}
          hideFooterSelectedRowCount
          checkboxSelection={showCheckbox}
          onSortModelChange={handleSortModelChange}
          paginationMode={"server"}
          rows={rows}
          isFetching={isFetching}
          columns={[...deptCertificatesColumns]}
          totalCount={totalCount}
          pageSize={_pageSize}
          rowsPerPageOptions={[5, 20, 50]}
          keepNonExistentRowsSelected={showCheckbox}
          isProblematic={isProblematic}
        />
      </div>

      <Box sx={{ display: "flex", flexDirection: "row", mt: "1em" }}>
        {selectedRows.length > 0 && (
          <>
            <Button
              variant="contained"
              onClick={() => setOpen(true)}
              disabled={isFetching}
              style={{
                marginRight: "1em",
                backgroundColor: "#1976D2",
                color: "#FFF",
              }}
            >
              Αποστολή ({selectedRows.length})
            </Button>
          </>
        )}
        {showCheckbox && (
          <Button
            variant="contained"
            onClick={() => setOpenAll(true)}
            disabled={isFetching || rows.length === 0}
            style={{
              marginRight: "1em",
              backgroundColor: `${!(isFetching || rows.length === 0) && "#1976D2"}`,
              color: `${!(isFetching || rows.length === 0) && "#FFF"}`,
            }}
          >
            Αποστολή Όλων
          </Button>
        )}
        <DownloadButton
          downloadUrl={downloadUrl}
          filename="Λίστα"
          buttonText="Λήψη Αρχείου"
          disabled={isFetching}
        />
      </Box>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Είστε σίγουροι ότι θέλετε να κάνετε επαναποστολή των (
            {selectedRows.length}) οφειλών που επιλέξατε.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" color="error" onClick={handleClose}>
            Ακύρωση
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleResendClick}
            autoFocus
          >
            Αποστολή
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openAll}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Είστε σίγουροι ότι θέλετε να κάνετε επαναποστολή{" "}
            <strong>όλων</strong> των οφειλών που επιλέξατε.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" color="error" onClick={handleClose}>
            Ακύρωση
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleResendAllClick}
            autoFocus
          >
            Αποστολή
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DeptCertificatesToAADEComponent;
